import { BackdropIndicator } from "@/components/common/BackdropIndicator";
import { SCSnackbar } from "@/components/sleep_checkup_v1/SCSnackbar";
import {
  ChangePageHandler,
  Pagination,
  RowsPerPageChangeHandler,
  SCEmptyTable,
  SCTable,
} from "@/components/sleep_checkup_v1/SCTable";
import { orgAxios } from "@/utils/axios";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  TableCell,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AxiosError } from "axios";
import { format, parseISO } from "date-fns";
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useRefreshMe } from "./OrgLayout";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LiteSurvey {
  project_id: string;
  survey_title: string;
  registered_count: number;
  report_sent_count: number;
  in_deadline_count: number;
  expired_count: number;
  registration_end_at: string | null;
}

interface CheckupProject {
  project_id: string;
  project_name: string;
  registered_count: number;
  not_measured_count: number;
  measuring_count: number;
  not_returned_count: number;
  report_issued_count: number;
}

interface PaginatedResponse<T> {
  is_activated: boolean;
  count: number;
  page_size: number;
  current: number;
  results: T[];
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

async function fetchLiteProjects(
  page: number,
  pageSize: number
): Promise<PaginatedResponse<LiteSurvey>> {
  const res = await orgAxios.get("/api/org/projects/lite/", {
    params: { page, page_size: pageSize },
  });
  return res.data;
}

async function fetchCheckupProjects(
  page: number,
  pageSize: number
): Promise<PaginatedResponse<CheckupProject>> {
  const res = await orgAxios.get("/api/org/projects/checkup/", {
    params: { page, page_size: pageSize },
  });
  return res.data;
}

interface DownloadPrepareResponse {
  password: string;
  download_url: string;
}

async function prepareDownload(
  projectId: string
): Promise<DownloadPrepareResponse> {
  const res = await orgAxios.post(
    `/api/org/projects/${projectId}/examinees/download/`
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractErrorMessages(error: unknown): string {
  if (error instanceof AxiosError && error.response?.data?.error_messages) {
    return (error.response.data.error_messages as string[]).join("\n");
  }
  return "エラーが発生しました。";
}

// ---------------------------------------------------------------------------
// Table headers
// ---------------------------------------------------------------------------

const LITE_HEADERS = [
  "survey_id",
  "survey_title",
  "登録者",
  "レポート送付済",
  "期限内",
  "期限切れ",
  "アカウント登録期限",
  "ダウンロード",
];

const CHECKUP_HEADERS = [
  "project_id",
  "pj",
  "合計",
  "未測定",
  "測定中",
  "測定後未返却",
  "レポート発行済",
  "ダウンロード",
];

// ---------------------------------------------------------------------------
// PasswordDialog
// ---------------------------------------------------------------------------

function PasswordDialog({
  open,
  password,
  onClose,
}: {
  open: boolean;
  password: string;
  onClose: () => void;
}) {
  const clipboardAvailable =
    window.isSecureContext && !!navigator.clipboard?.writeText;
  const defaultTooltip = clipboardAvailable
    ? "クリップボードにコピーする"
    : "この環境ではコピー機能を利用できません";
  const [tooltipMessage, setTooltipMessage] = useState(defaultTooltip);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleCopy = async () => {
    if (clipboardAvailable) {
      try {
        await navigator.clipboard.writeText(password);
        setTooltipMessage("コピーしました");
        setTimeout(() => setTooltipMessage(defaultTooltip), 2000);
      } catch {
        // コピー失敗時は何もしない
      }
    } else {
      setTooltipMessage("この環境ではコピー機能を利用できません");
      setTimeout(() => setTooltipMessage(defaultTooltip), 2000);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        paper: { sx: { borderRadius: "16px", width: 432 } },
      }}
    >
      <DialogTitle sx={{ pl: "24px" }}>
        <Stack direction="row" alignItems="center" sx={{ width: "100%" }}>
          <Typography variant="h4" sx={{ width: "100%" }}>
            閲覧用パスワード
          </Typography>
          <IconButton sx={{ width: "24px", height: "24px" }} onClick={onClose}>
            <CloseIcon sx={{ color: "#3D3D3D" }} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 4, px: 6 }}>
        <Typography sx={{ fontSize: 16, mb: 2, mt: 2 }}>
          ダウンロードしたファイルの閲覧には以下のパスワードが必要です。
          <strong>このポップアップを閉じると二度と表示されません。</strong>
          大切に保管してください。
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            p: 2,
            mt: "32px",
            mb: "40px",
            backgroundColor: "#F6F5F4",
            border: "1px solid #0000001F",
            borderRadius: 0,
          }}
        >
          <TextField
            value={password}
            fullWidth
            size="small"
            inputRef={inputRef}
            slotProps={{ input: { readOnly: true } }}
            sx={{
              fontFamily: "monospace",
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            }}
          />
          <Tooltip title={tooltipMessage}>
            <IconButton onClick={handleCopy} size="small">
              <ContentCopyIcon fontSize="small" sx={{ color: "#707070" }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// DownloadButton
// ---------------------------------------------------------------------------

function DownloadButton({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <Tooltip title="受診者一覧をダウンロード">
      <span>
        <IconButton
          onClick={onClick}
          disabled={disabled}
          size="small"
          sx={{ color: "#0056A1" }}
        >
          <FileDownloadOutlinedIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// LiteSurveyRow
// ---------------------------------------------------------------------------

function LiteSurveyRow({
  survey,
  onDownload,
  downloading,
}: {
  survey: LiteSurvey;
  onDownload: (projectId: string) => void;
  downloading: boolean;
}) {
  return (
    <TableRow sx={{ "& .MuiTableCell-root": { color: "#282828", p: "15px" } }}>
      <TableCell sx={{ width: "153px" }}>{survey.project_id}</TableCell>
      <TableCell sx={{ width: "320px" }}>{survey.survey_title}</TableCell>
      <TableCell sx={{ width: "153px" }}>{survey.registered_count}</TableCell>
      <TableCell sx={{ width: "153px" }}>{survey.report_sent_count}</TableCell>
      <TableCell sx={{ width: "153px" }}>{survey.in_deadline_count}</TableCell>
      <TableCell sx={{ width: "153px" }}>{survey.expired_count}</TableCell>
      <TableCell sx={{ width: "153px" }}>
        {survey.registration_end_at
          ? format(parseISO(survey.registration_end_at), "yyyy/MM/dd H:mm")
          : "-"}
      </TableCell>
      <TableCell align="center" sx={{ width: "120px" }}>
        <DownloadButton
          onClick={() => onDownload(survey.project_id)}
          disabled={downloading}
        />
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// CheckupProjectRow
// ---------------------------------------------------------------------------

function CheckupProjectRow({
  project,
  onDownload,
  downloading,
}: {
  project: CheckupProject;
  onDownload: (projectId: string) => void;
  downloading: boolean;
}) {
  return (
    <TableRow sx={{ "& .MuiTableCell-root": { color: "#282828", p: "15px" } }}>
      <TableCell>{project.project_id}</TableCell>
      <TableCell>{project.project_name}</TableCell>
      <TableCell>{project.registered_count}</TableCell>
      <TableCell>{project.not_measured_count}</TableCell>
      <TableCell>{project.measuring_count}</TableCell>
      <TableCell>{project.not_returned_count}</TableCell>
      <TableCell>{project.report_issued_count}</TableCell>
      <TableCell align="center">
        <DownloadButton
          onClick={() => onDownload(project.project_id)}
          disabled={downloading}
        />
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// OrgProjectList (main)
// ---------------------------------------------------------------------------

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export default function OrgProjectList() {
  const refreshMe = useRefreshMe();
  const [isActivated, setIsActivated] = useState(true);
  const [liteSurveys, setLiteSurveys] = useState<LiteSurvey[]>([]);
  const [liteCount, setLiteCount] = useState(0);
  const [litePage, setLitePage] = useState(DEFAULT_PAGE);
  const [litePageSize, setLitePageSize] = useState(DEFAULT_PAGE_SIZE);
  const [checkupProjects, setCheckupProjects] = useState<CheckupProject[]>([]);
  const [checkupCount, setCheckupCount] = useState(0);
  const [checkupPage, setCheckupPage] = useState(DEFAULT_PAGE);
  const [checkupPageSize, setCheckupPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [projectTab, setProjectTab] = useState(0);
  const [passwordDialog, setPasswordDialog] = useState<{
    open: boolean;
    password: string;
  }>({ open: false, password: "" });
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  const loadLiteProjects = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchLiteProjects(litePage, litePageSize);
      setIsActivated(data.is_activated);
      setLiteSurveys(data.results);
      setLiteCount(data.count);
    } catch (error) {
      setSnackbar({
        open: true,
        message: extractErrorMessages(error),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [litePage, litePageSize]);

  const loadCheckupProjects = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCheckupProjects(checkupPage, checkupPageSize);
      setIsActivated(data.is_activated);
      setCheckupProjects(data.results);
      setCheckupCount(data.count);
    } catch (error) {
      setSnackbar({
        open: true,
        message: extractErrorMessages(error),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [checkupPage, checkupPageSize]);

  useEffect(() => {
    if (projectTab === 0) {
      loadLiteProjects();
    } else {
      loadCheckupProjects();
    }
  }, [projectTab, loadLiteProjects, loadCheckupProjects]);

  const handleLitePageChange: ChangePageHandler = (newPage) => {
    setLitePage(newPage);
  };
  const handleLiteRowsPerPageChange: RowsPerPageChangeHandler = (newSize) => {
    setLitePageSize(newSize);
    setLitePage(DEFAULT_PAGE);
  };
  const handleCheckupPageChange: ChangePageHandler = (newPage) => {
    setCheckupPage(newPage);
  };
  const handleCheckupRowsPerPageChange: RowsPerPageChangeHandler = (
    newSize
  ) => {
    setCheckupPageSize(newSize);
    setCheckupPage(DEFAULT_PAGE);
  };

  const handleDownload = async (projectId: string) => {
    setDownloading(true);
    try {
      const { password, download_url } = await prepareDownload(projectId);
      // ブラウザに直接ダウンロードさせる（HTTP環境でのブロック回避）
      const fullUrl = new URL(download_url, orgAxios.defaults.baseURL).href;
      window.location.href = fullUrl;
      setPasswordDialog({ open: true, password });
    } catch (error) {
      setSnackbar({
        open: true,
        message: extractErrorMessages(error),
        severity: "error",
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleSnackbarClose = (
    _event: SyntheticEvent<Element, Event> | Event,
    reason: string
  ) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ px: 7, py: 6 }}>
      <Typography variant="h5" sx={{ mb: 4 }}>
        プロジェクト一覧
      </Typography>

      <Tabs
        value={projectTab}
        onChange={(_, v) => {
          refreshMe();
          setProjectTab(v);
          setLitePage(DEFAULT_PAGE);
          setLitePageSize(DEFAULT_PAGE_SIZE);
          setCheckupPage(DEFAULT_PAGE);
          setCheckupPageSize(DEFAULT_PAGE_SIZE);
        }}
        sx={{ mb: 4 }}
      >
        <Tab label="SLEEP COMPASS Light [Web問診]" />
        <Tab label="SLEEP COMPASS [デバイス測定]" />
      </Tabs>

      {loading ? (
        <BackdropIndicator open={loading} />
      ) : !isActivated ? (
        <SCEmptyTable
          headers={projectTab === 0 ? LITE_HEADERS : CHECKUP_HEADERS}
          message="現在表示できる項目がありません"
          subMessage={
            <Typography variant="body1" sx={{ color: "#A3A3A3" }}>
              アカウントの有効化手続き中です。
              <br />
              担当者からの電話による疎通確認が完了するまで、
              <br />
              プロジェクトにはアクセスできません。
            </Typography>
          }
        />
      ) : projectTab === 0 ? (
        liteSurveys.length > 0 ? (
          <SCTable<LiteSurvey>
            headers={LITE_HEADERS}
            list={liteSurveys}
            pagination={new Pagination(litePageSize, litePage, liteCount)}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onPageChange={handleLitePageChange}
            onRowsPerPageChange={handleLiteRowsPerPageChange}
            tableRow={(survey) => (
              <LiteSurveyRow
                key={survey.project_id}
                survey={survey}
                onDownload={handleDownload}
                downloading={downloading}
              />
            )}
          />
        ) : (
          <SCEmptyTable
            headers={LITE_HEADERS}
            message="現在表示できる項目がありません"
          />
        )
      ) : checkupProjects.length > 0 ? (
        <SCTable<CheckupProject>
          headers={CHECKUP_HEADERS}
          list={checkupProjects}
          pagination={
            new Pagination(checkupPageSize, checkupPage, checkupCount)
          }
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onPageChange={handleCheckupPageChange}
          onRowsPerPageChange={handleCheckupRowsPerPageChange}
          tableRow={(project) => (
            <CheckupProjectRow
              key={project.project_id}
              project={project}
              onDownload={handleDownload}
              downloading={downloading}
            />
          )}
        />
      ) : (
        <SCEmptyTable
          headers={CHECKUP_HEADERS}
          message="現在表示できる項目がありません"
        />
      )}

      {/* Password Dialog */}
      <PasswordDialog
        open={passwordDialog.open}
        password={passwordDialog.password}
        onClose={() => setPasswordDialog({ open: false, password: "" })}
      />

      {/* Snackbar */}
      <SCSnackbar
        open={snackbar.open}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      >
        {snackbar.message}
      </SCSnackbar>

      {/* Downloading Indicator */}
      <BackdropIndicator open={downloading} />
    </Box>
  );
}
